import React, { useCallback, useMemo } from 'react';
import { MdArrowForward } from 'react-icons/md';
import { usePromiseTracker } from 'react-promise-tracker';
import { LoadingMenu } from '../../../../../../components/LoadingSpinner';
import SelectPill from '../../../../../../components/SelectPill';
import { useElasticidadeCruzadaStore } from '../../ElasticidadeCruzada.store';
import type { CompetitorType, FilterOption, ProductFilterOption, SelectData } from '../../types';
import { useProductSelectOptions } from './ProductSelector';
import { MAX_SELECTED_LABEL_LENGTH, truncateSelectedLabel } from '../../utils/utils';
import styles from './styles.module.scss';

const COMPETITOR_TYPE_OPTIONS = [
  { label: 'Produto específico', value: 'PRODUTO_ESPECIFICO' },
  { label: 'Todos os concorrentes', value: 'TODOS' },
];

const mergeCachedAndSearchOptions = <T extends { value: unknown }>(cachedOptions: T[] = [], searchOptions: T[] = []) => {
  const combined = [...cachedOptions];
  searchOptions.forEach(option => {
    if (!combined.find(cached => cached.value === option.value)) {
      combined.push(option);
    }
  });
  return combined;
};

export const CompetitorSelector = () => {
  const { produtoReferencia, produtosCompetidores, tipoConcorrente, setFilterValue, setFilterList, setFilterCache, setState } = useElasticidadeCruzadaStore();
  const { productSelectOptions, productsSearch } = useProductSelectOptions();
  const { promiseInProgress: isLoadingProducts } = usePromiseTracker({ area: 'elasticidade-products' });

  const selectedProductBarcode = produtoReferencia.value?.[0] as string | undefined;

  const competitorsSelectOptions = useMemo<SelectData[]>(() => {
    if (!selectedProductBarcode) return [];

    const allProducts = mergeCachedAndSearchOptions(produtoReferencia.cache, productsSearch.data) as ProductFilterOption[];
    const selectedProduct = allProducts.find(p => p.value === selectedProductBarcode);

    if (!selectedProduct?.competitors || selectedProduct.competitors.length === 0) {
      return produtosCompetidores.cache || [];
    }

    const competitorOptions: FilterOption[] = selectedProduct.competitors.map(c => ({
      label: `${c.competitorGtin} - ${c.descricao || 'Sem descrição'}`,
      value: c.competitorGtin,
    }));

    const merged = mergeCachedAndSearchOptions(produtosCompetidores.cache, competitorOptions);

    const mainCompetitorGtins = new Set(selectedProduct.competitors.filter(c => c.mainCompetitor).map(c => c.competitorGtin));

    return [...merged].sort((a, b) => {
      const aIsMain = mainCompetitorGtins.has(String(a.value));
      const bIsMain = mainCompetitorGtins.has(String(b.value));
      if (aIsMain && !bIsMain) return -1;
      if (!aIsMain && bIsMain) return 1;
      return 0;
    });
  }, [selectedProductBarcode, produtoReferencia.cache, productsSearch.data, produtosCompetidores.cache]);

  const renderCompetitorSelectedValue = useCallback((_value: string, item: { label?: string }) => {
    const label = item?.label ?? '';
    const truncatedLabel = truncateSelectedLabel(label);

    return (
      <span className={styles.selectedPillValue} title={label.length > MAX_SELECTED_LABEL_LENGTH ? label : undefined}>
        {truncatedLabel}
      </span>
    );
  }, []);

  const renderCompetitorMenuItem = useCallback(
    (label: React.ReactNode, item: { value: string; label: string }) => {
      const allProducts = mergeCachedAndSearchOptions(produtoReferencia.cache, productsSearch.data) as ProductFilterOption[];
      const selectedProduct = allProducts.find(p => p.value === selectedProductBarcode);
      const competitor = selectedProduct?.competitors?.find(c => c.competitorGtin === item.value);
      const isMainCompetitor = competitor?.mainCompetitor ?? false;

      return (
        <div className={styles.competitorMenuItem}>
          <span className={styles.competitorMenuItemLabel}>{label}</span>
          {isMainCompetitor && <span className={styles.topCompetitorBadge}>TOP CONCORRENTE</span>}
        </div>
      );
    },
    [selectedProductBarcode, produtoReferencia.cache, productsSearch.data],
  );

  const handleCompetitorsChange = useCallback(
    (values: string[]) => {
      setFilterValue('produtosCompetidores', values);
      const selectedOptions = competitorsSelectOptions.filter(option => values.includes(String(option.value)));
      setFilterCache('produtosCompetidores', selectedOptions);
      setFilterValue('localizacao', []);
      setFilterList('localizacao', []);
      setFilterCache('localizacao', []);
    },
    [competitorsSelectOptions, setFilterCache, setFilterList, setFilterValue],
  );

  const handleCompetitorsClean = useCallback(() => {
    setFilterValue('produtosCompetidores', []);
    setFilterCache('produtosCompetidores', []);
    setFilterValue('localizacao', []);
    setFilterList('localizacao', []);
    setFilterCache('localizacao', []);
  }, [setFilterCache, setFilterList, setFilterValue]);

  const handleCompetitorTypeChange = useCallback(
    (value: CompetitorType) => {
      const nextType = value || 'PRODUTO_ESPECIFICO';
      setState('tipoConcorrente', nextType);
      setState('calculoConcorrente', nextType === 'TODOS' ? 'MEDIA' : null);
      setFilterValue('produtosCompetidores', []);
      setFilterCache('produtosCompetidores', []);
      setFilterValue('localizacao', []);
      setFilterList('localizacao', []);
      setFilterCache('localizacao', []);
    },
    [setFilterCache, setFilterList, setFilterValue, setState],
  );

  return (
    <div className={styles.filterSelectionWrapper}>
      <span className={styles.filterLabel}>CONCORRENTES</span>
      {/* @ts-ignore */}
      <SelectPill
        id="select-picker-pill"
        placeholder="Produto específico"
        data={COMPETITOR_TYPE_OPTIONS}
        value={tipoConcorrente}
        onSelect={handleCompetitorTypeChange}
        searchable={false}
        cleanable={false}
      />

      {tipoConcorrente === 'TODOS' ? (
        <span className={styles.staticPill} data-testid="all-competitors-static-pill">
          Média dos produtos concorrentes
        </span>
      ) : (
        <>
          <MdArrowForward className={styles.arrowIcon} />
          {/* @ts-ignore */}
          <SelectPill
            id="select-picker-pill"
            className={styles.truncatedSelectPill}
            placeholder="Selecione um produto"
            data={competitorsSelectOptions}
            value={produtosCompetidores.value?.[0] || null}
            onSelect={(value: string) => handleCompetitorsChange(value ? [value] : [])}
            onClean={handleCompetitorsClean}
            searchable
            cleanable
            renderMenuItem={renderCompetitorMenuItem}
            renderValue={renderCompetitorSelectedValue}
            locale={{
              noResultsText: isLoadingProducts ? ' ' : 'Nenhum item encontrado',
              searchPlaceholder: 'Buscar item',
            }}
            renderMenu={(menu: React.ReactNode) => (
              <span>
                <LoadingMenu area="elasticidade-products" />
                {menu}
              </span>
            )}
          />
        </>
      )}
    </div>
  );
};
